import type {
  TabProps,
  UseLocalStorageReducerDispatch
} from "@dealer-center/core-components";
import {
  Tabs,
  useLocalStorageReducerActions
} from "@dealer-center/core-components";
import styled from "styled-components";
import type { AccountSearchViewTabs } from "./types";
import type { dealerSearchIncoming } from "@dealer-center/ddc-pages-account-search-interface";
import { AccountList } from "./AccountList";
import { PagesClientSearchResult } from "./Results";
import { AccountSearchList } from "./AccountSearchList";
import { useContext } from "react";
import { AccountSearchContext } from "@dealer-center/ddc-pages-account-search-context";
import {
  useAccountFavourites,
  useAccountHistory
} from "@dealer-center/ddc-api-account-fetch";
import { useWindowDimensions } from "@dealer-center/core-custom-components";
import { shouldPinLinksMenu } from "@dealer-center/core-navbar";
const StyledExpandableTable = styled.div<{
  expanded: boolean;
  showingPinnedNav: boolean;
}>`
  width: ${({ expanded, showingPinnedNav }) =>
    getTableWidth(expanded, showingPinnedNav)};
`;
const StyledTabs = styled(Tabs)`
  button.btn.btn-link {
    background-color: inherit;
    &:hover {
      background-color: inherit;
    }
  }
  li.cx-tabs--active {
    button.btn.btn-link {
      background-color: inherit;
    }
  }
`;
interface AccountSearchTabsInterface {
  accountSearchHistory: {
    dispatch: UseLocalStorageReducerDispatch<dealerSearchIncoming>;
    items: dealerSearchIncoming[];
    enabled: boolean;
    setEnabled: (enabled: boolean) => void;
  };
  accountSearchFavorites: {
    enabled: boolean;
    persisted: [
      dealerSearchIncoming[],
      UseLocalStorageReducerDispatch<dealerSearchIncoming>
    ];
    items: dealerSearchIncoming[];
    setEnabled: (enabled: boolean) => void;
  };
  activeTab: AccountSearchViewTabs;
  setActiveTab: (tab: AccountSearchViewTabs) => void;
  shouldShowResults: boolean;
  shouldShowFullWidthTable: boolean;
}

interface InternalTabProps extends TabProps {
  value: AccountSearchViewTabs;
}

function getTableWidth(expanded: boolean, showingPinnedNav: boolean) {
  if (!expanded) return "100%";
  if (!showingPinnedNav) return "calc(100vw - 32px)";
  return "calc(100vw - 320px)";
}

export function AccountSearchTabs({
  accountSearchFavorites,
  accountSearchHistory,
  activeTab,
  setActiveTab,
  shouldShowResults,
  shouldShowFullWidthTable
}: AccountSearchTabsInterface) {
  const { accountFavorites } = useAccountFavourites();
  const { accountHistory, removeFromAccountHistory } = useAccountHistory();
  const windowDimensions = useWindowDimensions();
  const { accountSearchGet: accountSearchResult } =
    useContext(AccountSearchContext);
  const showingPinnedNav = shouldPinLinksMenu(windowDimensions);
  const tabs: InternalTabProps[] = [
    {
      value: "history",
      label: `History`,
      disabled: accountHistory?.length === 0,
      active: activeTab === "history",
      badge: true,
      badgeChildren: accountHistory?.length,
      component: (
        <AccountList
          accounts={accountHistory}
          title="Recently Accessed"
          onDelete={(account: string) => {
            removeFromAccountHistory(account);
          }}
        />
      )
    },
    {
      value: "favorites",
      active: activeTab === "favorites",
      disabled: accountFavorites?.length === 0,
      label: `Favorites`,
      badge: true,
      badgeChildren: accountFavorites?.length,
      component: (
        <AccountList
          shouldSortByAccountName
          accounts={accountFavorites}
          title="Favorites"
        />
      )
    },
    {
      value: "results",
      active: activeTab === "results",
      disabled: !shouldShowResults,
      label: `Results`,
      badge: true,
      badgeChildren: accountSearchResult?.data?.totalHits || "0",
      component: (
        <StyledExpandableTable
          expanded={shouldShowFullWidthTable}
          showingPinnedNav={showingPinnedNav}
        >
          <PagesClientSearchResult />
        </StyledExpandableTable>
      )
    }
  ];
  if (accountSearchHistory.enabled) {
    tabs.push({
      value: "searchHistory",
      active: activeTab === "searchHistory",
      disabled: accountSearchHistory.items.length === 0,
      label: `Search History`,
      badge: true,
      badgeChildren: accountSearchHistory.items.length,
      component: (
        <AccountSearchList
          accountSearchFavoritesPersisted={accountSearchFavorites.persisted}
          dispatchAccountSearchHistory={accountSearchHistory.dispatch}
          searches={accountSearchHistory.items}
          title="Search History"
          onDelete={(account: dealerSearchIncoming) => {
            accountSearchHistory.dispatch({
              action: useLocalStorageReducerActions.remove,
              payload: account
            });
          }}
        />
      )
    });
  }
  if (accountSearchFavorites.enabled) {
    tabs.push({
      value: "searchFavorites",
      active: activeTab === "searchFavorites",
      disabled: accountSearchFavorites.items.length === 0,
      label: `Search Favorites`,
      badge: true,
      badgeChildren: accountSearchFavorites.items.length,
      component: (
        <AccountSearchList
          accountSearchFavoritesPersisted={accountSearchFavorites.persisted}
          dispatchAccountSearchHistory={accountSearchHistory.dispatch}
          searches={accountSearchFavorites.items}
          title="Search Favorites"
        />
      )
    });
  }
  return <StyledTabs tabs={tabs} onActivate={setActiveTab} />;
}
